/**
 * Shared reads, row mappers and time helpers for the phone's SPACE screens.
 *
 * Three rules shape everything here.
 *
 *  1. **Reads come from the local mirror, writes go through `mutations.*`.**
 *     Nothing in this directory touches the network and nothing writes to Dexie
 *     directly.
 *
 *  2. **Time is injected.** Not one function or component below calls
 *     `Date.now()` or `new Date()` with no argument. Every "what time is it"
 *     read comes from a `TrustedClock` handed in from outside, because the
 *     tablet's own epoch may be a full hour wrong (docs/02 §4) and because a
 *     screen whose output depends on the ambient clock cannot be tested.
 *
 *  3. **The mirror is snake_case with ISO timestamps; the domain is camelCase
 *     with epoch ms.** The mappers below are the single crossing point, so no
 *     component ever has to know a column name.
 *
 * One deliberate extra: several `mutations.*` calls (queue join/leave/claim,
 * match.record, occupancy start/end, note.create, spacemode.set) append to the
 * outbox WITHOUT writing the local mirror — the mirror only learns about them
 * when sync pulls them back. Tapping "Join the queue" and watching nothing
 * happen is exactly the silent stall docs/01 §5.1 exists to prevent, so the
 * `overlay*` functions replay the *pending outbox* over the mirror on read.
 * That is a read-side projection, not a write, and it disappears by itself once
 * the real row arrives.
 */

import { useCallback, useEffect, useInsertionEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { liveQuery } from 'dexie'
import type { OfficeDb } from '../../lib/db.js'
import type { Mutation } from '../../lib/outbox.js'
import type { MutationContext } from '../../lib/mutations.js'
import {
  DEFAULT_ROLLOVER_HOUR,
  DEFAULT_SETTINGS,
  cairoParts,
  formatCairoTime12,
  rolloverDayBounds,
  rolloverDayOf,
} from '@office/domain'
import type {
  Booking,
  DayKey,
  Match,
  Note,
  Occupancy,
  PersonId,
  QueueEntry,
  Rating,
  Resource,
  ResourceId,
  SpaceModeValue,
  SupplyItem,
  SupplyState,
  TrustedClock,
} from '@office/domain'
import {
  applyMatch,
  claimQueueSlot,
  endOccupancy,
  joinQueue,
  leaveQueue,
  rebuildRatings,
  startOccupancy,
} from '@office/domain/resources'

// ---------------------------------------------------------------------------
// Common props
// ---------------------------------------------------------------------------

/**
 * What every space screen needs. `clock` is a prop rather than a module import
 * on purpose: it is the seam that makes these screens testable at a fixed
 * instant.
 */
export interface SpaceScreenProps {
  readonly db: OfficeDb
  readonly clock: TrustedClock
  /** The signed-in person. Attribution, never authorisation (docs/02 §8). */
  readonly actorId: string
}

export function mutationCtx(props: SpaceScreenProps): MutationContext {
  return { db: props.db, now: () => props.clock.now(), actorId: props.actorId }
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

type Row = Record<string, unknown>

/**
 * Subscribe to a Dexie query and re-run it whenever anything it read changes.
 *
 * `repo.useQuery` subscribes to `db.on('changes')`, which only exists with the
 * dexie-observable addon and throws on Dexie 4 — see the note in the handover.
 * `liveQuery` is the supported Dexie 4 equivalent and tracks reads precisely,
 * so this also re-renders when the sync engine writes rows from the server.
 */
export function useLive<T>(
  db: OfficeDb,
  run: (db: OfficeDb) => Promise<T>,
  initial: T,
  deps: readonly unknown[] = [],
): T {
  const [value, setValue] = useState<T>(initial)
  const runRef = useRef(run)
  runRef.current = run

  useEffect(() => {
    const sub = liveQuery(() => runRef.current(db)).subscribe({
      next: v => setValue(v as T),
      error: () => {
        /* a mirror read that fails leaves the last good value on screen */
      },
    })
    return () => sub.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, ...deps])

  return value
}

/**
 * The current instant, from the TRUSTED clock, re-read every `periodMs`.
 *
 * Pass `periodMs = 0` to freeze it — which is what tests do, so a countdown
 * renders one deterministic frame.
 */
export function useNow(clock: TrustedClock, periodMs = 1000): number {
  const [now, setNow] = useState<number>(() => clock.now())
  useEffect(() => {
    setNow(clock.now())
    if (periodMs <= 0) return
    const id = setInterval(() => setNow(clock.now()), periodMs)
    return () => clearInterval(id)
  }, [clock, periodMs])
  return now
}

/** Everything the screens read out of the mirror, in one subscription. */
export interface SpaceSnapshot {
  readonly people: SpacePerson[]
  readonly presenceEvents: PresenceEventRow[]
  readonly resources: Resource[]
  readonly bookings: Booking[]
  readonly occupancies: Occupancy[]
  readonly queue: QueueEntry[]
  readonly matches: Match[]
  readonly ratings: Rating[]
  readonly supplies: SupplyItem[]
  readonly supplyEvents: SupplyEventRow[]
  readonly notes: Note[]
  readonly visits: VisitRow[]
  readonly rooms: RoomOption[]
  readonly spaceMode: StoredSpaceMode
  readonly settings: SpaceSettings
  readonly pending: Mutation[]
}

export const EMPTY_SNAPSHOT: SpaceSnapshot = {
  people: [],
  presenceEvents: [],
  resources: [],
  bookings: [],
  occupancies: [],
  queue: [],
  matches: [],
  ratings: [],
  supplies: [],
  supplyEvents: [],
  notes: [],
  visits: [],
  rooms: [],
  spaceMode: { current: 'work', setBy: null, setAt: null, auto: false },
  settings: {
    dayRolloverHour: DEFAULT_ROLLOVER_HOUR,
    queueClaimWindowMinutes: DEFAULT_SETTINGS.queueClaimWindowMinutes,
    guestArrivingLeadMinutes: DEFAULT_SETTINGS.guestArrivingLeadMinutes,
  },
  pending: [],
}

/**
 * One live read of everything the space screens need, with the pending outbox
 * already folded in. Screens then do pure derivation on top.
 */
export function useSpace(db: OfficeDb): SpaceSnapshot {
  return useLive(db, readSpace, EMPTY_SNAPSHOT)
}

/**
 * The mirror holds the WHOLE dataset — three years, tens of thousands of rows
 * (docs/02 §8) — and `liveQuery` re-runs this on every relevant write. The
 * append-only logs are therefore read newest-first with a bound rather than in
 * full. The limits are far beyond anything three owners produce in a year, so
 * nothing on screen is ever truncated in practice, and the cost of a tap stops
 * growing with the age of the office.
 *
 * `matches` is deliberately NOT bounded: head-to-head records and a rebuilt
 * ratings table are only correct over the complete log.
 */
const RECENT_PRESENCE_EVENTS = 2000
const RECENT_VISITS = 200
const RECENT_BOOKINGS = 400
const RECENT_SUPPLY_EVENTS = 1000

export async function readSpace(db: OfficeDb): Promise<SpaceSnapshot> {
  const [
    people,
    members,
    guests,
    presence,
    resources,
    bookings,
    occupancies,
    queue,
    matches,
    ratings,
    supplies,
    supplyEvents,
    notes,
    visits,
    rooms,
    modeRows,
    settingsRows,
    pending,
  ] = await Promise.all([
    db.people.toArray(),
    db.members.toArray(),
    db.guests.toArray(),
    db.presence_events.orderBy('at').reverse().limit(RECENT_PRESENCE_EVENTS).toArray(),
    db.resources.toArray(),
    db.bookings.orderBy('starts_at').reverse().limit(RECENT_BOOKINGS).toArray(),
    db.occupancy.toArray(),
    db.queue_entries.toArray(),
    db.matches.toArray(),
    db.ratings.toArray(),
    db.supply_items.toArray(),
    db.supply_events.orderBy('at').reverse().limit(RECENT_SUPPLY_EVENTS).toArray(),
    // A TOMBSTONED note is deleted, not merely expired, and must never reach a
    // screen. `syncOnce` drops the local row as soon as the tombstone arrives,
    // so this normally matches everything; it is a backstop for any path that
    // puts a server row into the mirror without going through that branch.
    // A note resurrected by `restoreBackup` is NOT one of them — it carries no
    // `deleted_at` for this to catch — which is why the restore forgets the
    // pull cursors instead (backup.ts).
    db.notes.filter(n => n.deleted_at == null).toArray(),
    db.visits.orderBy('arrived_at').reverse().limit(RECENT_VISITS).toArray(),
    db.room_labels.toArray(),
    db.space_mode.toArray(),
    db.settings.toArray(),
    db.outbox.toArray(),
  ])

  const sortedPending = [...pending].sort((a, b) => a.queuedAt - b.queuedAt || cmp(a.id, b.id))

  const presenceEvents = presence.map(toPresenceEvent).sort((a, b) => a.at - b.at || cmp(a.id, b.id))
  // Ids something has actually happened to; the tiebreak for duplicate rows.
  const liveIds = new Set(presenceEvents.map(e => String(e.personId)))

  return {
    people: dedupeByName(toPeople(people, members, guests), liveIds),
    presenceEvents,
    resources: resources.map(toResource),
    bookings: bookings.map(toBooking),
    occupancies: overlayOccupancy(occupancies.map(toOccupancy), sortedPending),
    queue: overlayQueue(queue.map(toQueueEntry), sortedPending),
    matches: overlayMatches(matches.map(toMatch), sortedPending),
    ratings: ratings.map(toRating),
    supplies: supplies.map(toSupplyItem),
    supplyEvents: supplyEvents.map(toSupplyEvent),
    notes: overlayNotes(notes.map(toNote), sortedPending),
    visits: visits.map(toVisit),
    rooms: rooms.map(toRoomOption),
    spaceMode: overlaySpaceMode(readSpaceMode(modeRows), sortedPending),
    settings: readSettings(settingsRows),
    pending: sortedPending,
  }
}

// ---------------------------------------------------------------------------
// Row mappers — the one place that knows the mirror's column names
// ---------------------------------------------------------------------------

const cmp = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v))
const strOrNull = (v: unknown): string | null =>
  v === null || v === undefined || v === '' ? null : String(v)
const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback)
const num = (v: unknown, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : fallback
}
const list = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : [])

/**
 * Epoch milliseconds from whatever the mirror holds.
 *
 * The mirror stores ISO strings (that is what `mutations.*` writes and what the
 * sync payloads carry), but a numeric epoch must survive too — and anything
 * unparseable becomes `null` rather than `NaN`, because the domain refuses a
 * non-finite time and a NaN in a claim window is a queue that never drains.
 */
export function toMs(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string' && v !== '') {
    const parsed = Date.parse(v)
    if (Number.isFinite(parsed)) return parsed
    const asNumber = Number(v)
    return Number.isFinite(asNumber) ? asNumber : null
  }
  return null
}

const msOr = (v: unknown, fallback: number): number => toMs(v) ?? fallback

export interface SpacePerson {
  readonly id: PersonId
  readonly kind: 'member' | 'guest'
  readonly displayName: string
  readonly photoRef: string | null
  /** 'MM-DD'; the year is deliberately not kept. */
  readonly birthday: string | null
  readonly company: string | null
  readonly hostMemberId: string | null
  readonly hasApp: boolean
}

/** Normalise 'YYYY-MM-DD' or 'MM-DD' to 'MM-DD'. */
export function birthdayKey(raw: unknown): string | null {
  const s = strOrNull(raw)
  if (s === null) return null
  const m = /(\d{2})-(\d{2})$/.exec(s)
  return m ? `${m[1]}-${m[2]}` : null
}

/**
 * One row per human. See `dedupePeople` in lib/repo.ts for why the mirror can
 * hold three of each: nothing removes a person the server stopped sending, so a
 * device that synced against an earlier seed shows three Bayos with three
 * separate check-in buttons. Deduped here so every screen fed by `useSpace`
 * agrees with the wall, which dedupes through the repo.
 */
function dedupeByName(rows: SpacePerson[], live: ReadonlySet<string>): SpacePerson[] {
  const byName = new Map<string, SpacePerson>()
  for (const p of rows) {
    const key = p.displayName.trim().toLowerCase()
    const seen = byName.get(key)
    if (seen === undefined) { byName.set(key, p); continue }
    const better = live.has(String(p.id)) && !live.has(String(seen.id))
    if (better) byName.set(key, p)
  }
  return [...byName.values()]
}

export function toPeople(people: Row[], members: Row[], guests: Row[]): SpacePerson[] {
  const memberById = new Map(members.map(m => [str(m.person_id), m]))
  const guestById = new Map(guests.map(g => [str(g.person_id), g]))
  return people
    .filter(p => p.archived_at === null || p.archived_at === undefined)
    .map(p => {
      const id = str(p.id)
      const guest = guestById.get(id)
      const member = memberById.get(id)
      const kind = str(p.kind) === 'guest' || (guest !== undefined && member === undefined)
        ? ('guest' as const)
        : ('member' as const)
      return {
        id: id as PersonId,
        kind,
        displayName: str(p.display_name) || 'Unknown',
        photoRef: strOrNull(p.photo_ref),
        birthday: birthdayKey(p.birthday),
        company: strOrNull(guest?.company),
        hostMemberId: strOrNull(guest?.host_member_id),
        hasApp: bool(member?.has_app, kind === 'member'),
      }
    })
    .sort((a, b) => cmp(a.displayName, b.displayName) || cmp(a.id, b.id))
}

export interface PresenceEventRow {
  readonly id: string
  readonly personId: PersonId
  readonly type: 'in' | 'out'
  readonly at: number
  readonly source: string
}

export function toPresenceEvent(r: Row): PresenceEventRow {
  return {
    id: str(r.id),
    personId: str(r.person_id) as PersonId,
    type: str(r.type) === 'out' ? 'out' : 'in',
    at: msOr(r.at, 0),
    source: str(r.source) || 'app',
  }
}

export function toResource(r: Row): Resource {
  const kind = str(r.kind) === 'room' ? ('room' as const) : ('equipment' as const)
  return {
    id: str(r.id) as ResourceId,
    name: str(r.name) || 'Resource',
    kind,
    floorplanRoomId: null,
    capacity: r.capacity === null || r.capacity === undefined ? null : num(r.capacity, 0),
    bookable: bool(r.bookable, true),
    // A tablet mirror seeded before these columns existed still has to work:
    // the table and the PlayStation are equipment, and equipment is what people
    // queue for and play on.
    queueable: bool(r.queueable, kind === 'equipment'),
    competitive: bool(r.competitive, kind === 'equipment'),
    colour: str(r.colour),
    active: bool(r.active, true),
  }
}

export function toBooking(r: Row): Booking {
  return {
    id: str(r.id) as Booking['id'],
    resourceId: str(r.resource_id) as ResourceId,
    createdBy: str(r.created_by) as Booking['createdBy'],
    title: str(r.title),
    startsAt: msOr(r.starts_at, 0),
    endsAt: msOr(r.ends_at, 0),
    kind: (['meeting', 'guest', 'session', 'blocked'] as const).includes(
      str(r.kind) as Booking['kind'],
    )
      ? (str(r.kind) as Booking['kind'])
      : 'meeting',
    attendeeIds: list(r.attendee_ids) as Booking['attendeeIds'],
    guestIds: list(r.guest_ids) as Booking['guestIds'],
    linkedProposalId: null,
    status: str(r.status) === 'cancelled' ? 'cancelled' : 'confirmed',
    createdAt: msOr(r.created_at, 0),
  }
}

export function toOccupancy(r: Row): Occupancy {
  return {
    id: str(r.id),
    resourceId: str(r.resource_id) as ResourceId,
    startedBy: str(r.started_by) as PersonId,
    startedAt: msOr(r.started_at, 0),
    endedAt: toMs(r.ended_at),
    source: str(r.source) === 'booking' ? 'booking' : 'manual',
  }
}

export function toQueueEntry(r: Row): QueueEntry {
  const status = str(r.status)
  return {
    id: str(r.id) as QueueEntry['id'],
    resourceId: str(r.resource_id) as ResourceId,
    personId: str(r.person_id) as PersonId,
    joinedAt: msOr(r.joined_at, 0),
    status: (['waiting', 'claimed', 'playing', 'expired', 'left'] as const).includes(
      status as QueueEntry['status'],
    )
      ? (status as QueueEntry['status'])
      : 'waiting',
    claimWindowEndsAt: toMs(r.claim_window_ends_at),
    resolvedAt: toMs(r.resolved_at),
  }
}

export function toMatch(r: Row): Match {
  const scoreA = num(r.score_a, 0)
  const scoreB = num(r.score_b, 0)
  return {
    id: str(r.id) as Match['id'],
    resourceId: str(r.resource_id) as ResourceId,
    playedAt: msOr(r.played_at, 0),
    kind: str(r.kind) === 'doubles' ? 'doubles' : 'singles',
    sideA: list(r.side_a) as PersonId[],
    sideB: list(r.side_b) as PersonId[],
    scoreA,
    scoreB,
    winner: str(r.winner) === 'b' ? 'b' : str(r.winner) === 'a' ? 'a' : scoreA > scoreB ? 'a' : 'b',
    recordedBy: str(r.recorded_by) as Match['recordedBy'],
    verifiedBy: null,
  }
}

export function toRating(r: Row): Rating {
  return {
    personId: str(r.person_id) as PersonId,
    resourceId: str(r.resource_id) as ResourceId,
    rating: num(r.rating, 1200),
    matchesPlayed: num(r.matches_played, 0),
    provisional: bool(r.provisional, true),
    wins: num(r.wins, 0),
    losses: num(r.losses, 0),
    currentStreak: num(r.current_streak, 0),
    bestStreak: num(r.best_streak, 0),
  }
}

export function toSupplyItem(r: Row): SupplyItem {
  const state = str(r.state)
  return {
    id: str(r.id) as SupplyItem['id'],
    name: str(r.name) || 'Item',
    category: str(r.category) || 'General',
    icon: strOrNull(r.icon),
    state: state === 'low' || state === 'out' ? (state as SupplyState) : 'ok',
    lastFlaggedAt: toMs(r.last_flagged_at),
    lastFlaggedBy: strOrNull(r.last_flagged_by) as PersonId | null,
    lastRestockedAt: toMs(r.last_restocked_at),
    active: bool(r.active, true),
  }
}

export interface SupplyEventRow {
  readonly id: string
  readonly itemId: string
  readonly state: SupplyState
  readonly at: number
  readonly by: string
}

export function toSupplyEvent(r: Row): SupplyEventRow {
  const state = str(r.state)
  return {
    id: str(r.id),
    itemId: str(r.item_id),
    state: state === 'low' || state === 'out' ? (state as SupplyState) : 'ok',
    at: msOr(r.at, 0),
    by: str(r.by),
  }
}

export function toNote(r: Row): Note {
  return {
    id: str(r.id) as Note['id'],
    authorId: str(r.author_id) as PersonId,
    body: str(r.body),
    colour: str(r.colour),
    position: null,
    pinnedToRoomId: strOrNull(r.pinned_to_room_id) as Note['pinnedToRoomId'],
    createdAt: msOr(r.created_at, 0),
    expiresAt: toMs(r.expires_at),
  }
}

export interface VisitRow {
  readonly id: string
  readonly guestId: string
  readonly arrivedAt: number
  readonly departedAt: number | null
  readonly hostMemberId: string | null
}

export function toVisit(r: Row): VisitRow {
  return {
    id: str(r.id),
    guestId: str(r.guest_id),
    arrivedAt: msOr(r.arrived_at, 0),
    departedAt: toMs(r.departed_at),
    hostMemberId: strOrNull(r.host_member_id),
  }
}

export interface RoomOption {
  readonly id: string
  readonly name: string
}

export function toRoomOption(r: Row): RoomOption {
  return { id: str(r.room_id), name: str(r.name) || str(r.label) || str(r.room_id) }
}

export interface StoredSpaceMode {
  readonly current: SpaceModeValue
  readonly setBy: string | null
  readonly setAt: number | null
  readonly auto: boolean
}

export function readSpaceMode(rows: Row[]): StoredSpaceMode {
  const row = rows[0]
  const raw = str(row?.current)
  const current: SpaceModeValue = (['work', 'chill', 'gaming', 'empty'] as const).includes(
    raw as SpaceModeValue,
  )
    ? (raw as SpaceModeValue)
    : 'work'
  return {
    current,
    setBy: strOrNull(row?.set_by),
    setAt: toMs(row?.set_at),
    auto: bool(row?.auto, false),
  }
}

export interface SpaceSettings {
  readonly dayRolloverHour: number
  readonly queueClaimWindowMinutes: number
  readonly guestArrivingLeadMinutes: number
}

export function readSettings(rows: Row[]): SpaceSettings {
  const row = rows[0] ?? {}
  return {
    dayRolloverHour: num(row.day_rollover_hour, DEFAULT_ROLLOVER_HOUR),
    queueClaimWindowMinutes: num(
      row.queue_claim_window_minutes,
      DEFAULT_SETTINGS.queueClaimWindowMinutes,
    ),
    guestArrivingLeadMinutes: num(
      row.guest_arriving_lead_minutes,
      DEFAULT_SETTINGS.guestArrivingLeadMinutes,
    ),
  }
}

// ---------------------------------------------------------------------------
// The pending-outbox overlay
// ---------------------------------------------------------------------------

const payloadStr = (m: Mutation, key: string): string => str(m.payload[key])
const payloadList = (m: Mutation, key: string): string[] => list(m.payload[key])
const payloadMs = (m: Mutation, key: string): number => msOr(m.payload[key], m.queuedAt)

/**
 * Replay pending queue mutations over the mirror.
 *
 * `mutations.joinQueue` carries no entry id, so the mutation's own UUIDv7 is
 * used — which is exactly what the server will mint from it, and which sorts by
 * queue time for free.
 */
export function overlayQueue(entries: QueueEntry[], pending: readonly Mutation[]): QueueEntry[] {
  let out = entries
  for (const m of pending) {
    if (m.kind === 'queue.join') {
      out = joinQueue(out, {
        id: m.id as QueueEntry['id'],
        resourceId: payloadStr(m, 'resourceId') as ResourceId,
        personId: payloadStr(m, 'personId') as PersonId,
        joinedAt: payloadMs(m, 'at'),
      })
    } else if (m.kind === 'queue.leave') {
      out = leaveQueue(out, payloadStr(m, 'entryId') as QueueEntry['id'], payloadMs(m, 'at'))
    } else if (m.kind === 'queue.claim') {
      out = claimQueueSlot(out, payloadStr(m, 'entryId') as QueueEntry['id'], payloadMs(m, 'at'))
    }
  }
  return out
}

export function overlayOccupancy(
  occupancies: Occupancy[],
  pending: readonly Mutation[],
): Occupancy[] {
  let out = occupancies
  for (const m of pending) {
    if (m.kind === 'occupancy.start') {
      out = startOccupancy(out, {
        id: m.id,
        resourceId: payloadStr(m, 'resourceId') as ResourceId,
        startedBy: payloadStr(m, 'by') as PersonId,
        startedAt: payloadMs(m, 'at'),
        source: 'manual',
      })
    } else if (m.kind === 'occupancy.end') {
      out = endOccupancy(out, payloadStr(m, 'resourceId') as ResourceId, payloadMs(m, 'at'))
    }
  }
  return out
}

export function overlayMatches(matches: Match[], pending: readonly Mutation[]): Match[] {
  const out = [...matches]
  for (const m of pending) {
    if (m.kind !== 'match.record') continue
    const scoreA = num(m.payload.scoreA, 0)
    const scoreB = num(m.payload.scoreB, 0)
    out.push({
      id: m.id as Match['id'],
      resourceId: payloadStr(m, 'resourceId') as ResourceId,
      playedAt: payloadMs(m, 'playedAt'),
      kind: payloadStr(m, 'kind') === 'doubles' ? 'doubles' : 'singles',
      sideA: payloadList(m, 'sideA') as PersonId[],
      sideB: payloadList(m, 'sideB') as PersonId[],
      scoreA,
      scoreB,
      winner: payloadStr(m, 'winner') === 'b' ? 'b' : 'a',
      recordedBy: payloadStr(m, 'recordedBy') as Match['recordedBy'],
      verifiedBy: null,
    })
  }
  return out
}

/**
 * Deletions are applied AFTER creations, not before.
 *
 * Post a note with the network down and then think better of it: both
 * mutations sit in the outbox, and `mutations.deleteNote`'s local write finds
 * nothing to delete because the note was never in the mirror. Filtering first
 * would therefore delete nothing and then re-project the note, and it would sit
 * on the board — undeletable, since every further delete replays the same way —
 * until sync. Ordering the two passes this way makes the pair cancel.
 */
export function overlayNotes(notes: Note[], pending: readonly Mutation[]): Note[] {
  const out = [...notes]
  for (const m of pending) {
    if (m.kind !== 'note.create') continue
    out.push({
      id: m.id as Note['id'],
      authorId: payloadStr(m, 'authorId') as PersonId,
      body: payloadStr(m, 'body'),
      colour: payloadStr(m, 'colour'),
      position: null,
      pinnedToRoomId: (strOrNull(m.payload.pinnedToRoomId) as Note['pinnedToRoomId']) ?? null,
      createdAt: payloadMs(m, 'createdAt'),
      expiresAt: null,
    })
  }
  const deleted = new Set(
    pending.filter(m => m.kind === 'note.delete').map(m => payloadStr(m, 'noteId')),
  )
  return deleted.size === 0 ? out : out.filter(n => !deleted.has(String(n.id)))
}

export function overlaySpaceMode(
  mode: StoredSpaceMode,
  pending: readonly Mutation[],
): StoredSpaceMode {
  let out = mode
  for (const m of pending) {
    if (m.kind !== 'spacemode.set') continue
    const raw = payloadStr(m, 'mode')
    if (!(['work', 'chill', 'gaming', 'empty'] as const).includes(raw as SpaceModeValue)) continue
    out = {
      current: raw as SpaceModeValue,
      setBy: strOrNull(m.payload.by),
      setAt: payloadMs(m, 'at'),
      auto: bool(m.payload.auto, false),
    }
  }
  return out
}

// ---------------------------------------------------------------------------
// Presence (docs/01 §2)
// ---------------------------------------------------------------------------

/** After this long with no detection, a present person is shown as stale. */
export const STALE_AFTER_MS = 90 * 60_000

/**
 * Who wins a tie at the same instant. Lower is applied first, so a manual tap
 * lands after the camera and overwrites it. `day_rollover` is the sweep and
 * outranks both: if it and a detection share a timestamp, the session closed.
 */
function sourceRank(source: string): number {
  if (source === 'face') return 0
  if (source === 'day_rollover') return 2
  return 1
}

export interface PresenceEntry {
  readonly personId: PersonId
  readonly present: boolean
  /** Start of the current session, in epoch ms. */
  readonly since: number | null
  /** Latest event of any kind — the "last detected" the board shows. */
  readonly lastSeen: number | null
  readonly lastSource: string | null
}

/**
 * Who is in, by the OFFICE day rather than the calendar day.
 *
 * An `in` from before the current rollover day is not presence: `day_rollover`
 * closes any session still open at 05:00 (docs/01 §2), and on a tablet that was
 * offline at 05:00 the sweep may not have been recorded yet. Deriving it here
 * means a forgotten checkout from Tuesday never shows someone as present on
 * Thursday.
 *
 * The same rule has to apply while REPLAYING, not only at the end. A session is
 * bounded by its own office day, so a re-detection after 05:00 opens a NEW
 * session rather than extending last night's — otherwise the person who worked
 * through the night and was seen by the camera a minute ago inherits a `since`
 * from yesterday, fails the boundary check below, and is shown as away while
 * standing in the room. Which is the "someone shut in a room for hours is
 * normal" case §2 is built around, arriving at it from the other direction.
 */
export function presenceAt(
  events: readonly PresenceEventRow[],
  now: number,
  rolloverHour: number = DEFAULT_ROLLOVER_HOUR,
): Map<PersonId, PresenceEntry> {
  const { startUtc } = rolloverDayBounds(rolloverDayOf(now, rolloverHour), rolloverHour)
  // "Manual check-in/out from the tablet or the app always wins over the
  // camera" (docs/01 §2). Time decides first; where two events share an
  // instant — checking out on the tablet while the camera watches you leave —
  // the manual one is applied last and therefore wins, rather than the
  // arbitrary id tiebreak deciding it.
  const ordered = [...events]
    // "Face recognition may emit `in`, and must NEVER emit `out`" — docs/01 §2,
    // and the doc calls the asymmetry deliberate and permanent. `face/events.ts`
    // cannot produce one, but the mirror is whatever the server sent: one bad
    // row, one future camera bug, one hand-written fixture, and somebody sitting
    // in a room the camera does not cover is shown as gone. Dropping it on the
    // read side makes the rule structural on both sides of the wire rather than
    // a property of the writer.
    .filter(e => !(e.type === 'out' && e.source === 'face'))
    .sort((a, b) => a.at - b.at || sourceRank(a.source) - sourceRank(b.source) || cmp(a.id, b.id))
  const state = new Map<PersonId, PresenceEntry>()

  // Events are ascending, so one cached day window serves whole runs of them:
  // this costs one bounds computation per office day present in the log, not
  // one per event.
  let dayStart = Number.POSITIVE_INFINITY
  let dayEnd = Number.NEGATIVE_INFINITY
  const officeDayStartOf = (at: number): number => {
    if (at < dayStart || at >= dayEnd) {
      const bounds = rolloverDayBounds(rolloverDayOf(at, rolloverHour), rolloverHour)
      dayStart = bounds.startUtc
      dayEnd = bounds.endUtc
    }
    return dayStart
  }

  for (const e of ordered) {
    const prev = state.get(e.personId)
    if (e.type === 'in') {
      const continuing =
        prev?.present === true && prev.since !== null && prev.since >= officeDayStartOf(e.at)
      state.set(e.personId, {
        personId: e.personId,
        present: true,
        since: continuing ? prev.since : e.at,
        lastSeen: e.at,
        lastSource: e.source,
      })
    } else {
      state.set(e.personId, {
        personId: e.personId,
        present: false,
        since: null,
        lastSeen: e.at,
        lastSource: e.source,
      })
    }
  }

  for (const [id, entry] of state) {
    if (entry.present && (entry.since === null || entry.since < startUtc)) {
      state.set(id, { ...entry, present: false, since: null })
    }
  }
  return state
}

export function presenceOf(
  events: readonly PresenceEventRow[],
  personId: string,
  now: number,
  rolloverHour: number = DEFAULT_ROLLOVER_HOUR,
): PresenceEntry {
  return (
    presenceAt(events, now, rolloverHour).get(personId as PersonId) ?? {
      personId: personId as PersonId,
      present: false,
      since: null,
      lastSeen: null,
      lastSource: null,
    }
  )
}

export const isStale = (entry: PresenceEntry, now: number): boolean =>
  entry.present && entry.lastSeen !== null && now - entry.lastSeen > STALE_AFTER_MS

/** Nobody detected and nobody checked in — the only way `empty` is ever set. */
export function isSpaceEmpty(
  events: readonly PresenceEventRow[],
  now: number,
  rolloverHour: number = DEFAULT_ROLLOVER_HOUR,
): boolean {
  for (const entry of presenceAt(events, now, rolloverHour).values()) {
    if (entry.present) return false
  }
  return true
}

// ---------------------------------------------------------------------------
// Supplies cadence (docs/01 §8)
// ---------------------------------------------------------------------------

export const MS_PER_DAY = 86_400_000

/**
 * "Coffee usually lasts 18 days." Mean gap between restocks, or null when there
 * are fewer than two to measure between — a cadence from one data point is a
 * guess dressed up as a fact.
 */
export function restockCadenceDays(
  events: readonly SupplyEventRow[],
  itemId: string,
): number | null {
  const restocks = events
    .filter(e => e.itemId === itemId && e.state === 'ok')
    .map(e => e.at)
    .filter(at => Number.isFinite(at))
    .sort((a, b) => a - b)
  if (restocks.length < 2) return null
  let total = 0
  for (let i = 1; i < restocks.length; i++) total += restocks[i]! - restocks[i - 1]!
  const days = total / (restocks.length - 1) / MS_PER_DAY
  return days > 0 ? Math.round(days) : null
}

export function daysSince(at: number | null, now: number): number | null {
  if (at === null || !Number.isFinite(at) || !Number.isFinite(now)) return null
  return Math.max(0, Math.floor((now - at) / MS_PER_DAY))
}

// ---------------------------------------------------------------------------
// Formatting — all of it takes `now` explicitly
// ---------------------------------------------------------------------------

export const timeOfDay = (ms: number): string => formatCairoTime12(ms)

/** '3h 12m', '48m', '0m'. Never negative. */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0m'
  const totalMinutes = Math.floor(ms / 60_000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours === 0) return `${minutes}m`
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`
}

/** 'just now', '12m ago', '3h ago', '2d ago'. */
export function formatAgo(at: number | null, now: number): string {
  if (at === null || !Number.isFinite(at) || !Number.isFinite(now)) return 'unknown'
  const delta = now - at
  if (delta < 60_000) return 'just now'
  if (delta < MS_PER_DAY) return `${formatDuration(delta)} ago`
  const days = Math.floor(delta / MS_PER_DAY)
  return days === 1 ? 'yesterday' : `${days}d ago`
}

/** 'M:SS' for a claim-window countdown. Clamped at 0:00. */
export function formatCountdown(msRemaining: number): string {
  const clamped = Number.isFinite(msRemaining) ? Math.max(0, msRemaining) : 0
  const totalSeconds = Math.ceil(clamped / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

/** '15 Mar' in Cairo terms — never the device's locale or timezone. */
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

export function formatDayMonth(ms: number): string {
  if (!Number.isFinite(ms)) return '—'
  const p = cairoParts(ms)
  return `${p.day} ${MONTHS[p.month - 1] ?? '?'}`
}

/** 'MM-DD' → '15 Mar', for birthdays, which carry no year. */
export function formatBirthday(mmdd: string): string {
  const m = /^(\d{2})-(\d{2})$/.exec(mmdd)
  if (m === null) return mmdd
  return `${Number(m[2])} ${MONTHS[Number(m[1]) - 1] ?? '?'}`
}

export function todayKey(now: number, rolloverHour: number = DEFAULT_ROLLOVER_HOUR): DayKey {
  return rolloverDayOf(now, rolloverHour)
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return (parts[0] ?? '?').slice(0, 2).toUpperCase()
  return `${(parts[0] ?? '')[0] ?? ''}${(parts[1] ?? '')[0] ?? ''}`.toUpperCase()
}

/** Name lookup that degrades to something readable rather than a raw UUID. */
export function nameLookup(people: readonly SpacePerson[]): (id: string) => string {
  const byId = new Map(people.map(p => [String(p.id), p.displayName]))
  return id => byId.get(id) ?? 'Someone'
}

// ---------------------------------------------------------------------------
// Ladder helpers
// ---------------------------------------------------------------------------

/**
 * Standings that already include the match you just recorded.
 *
 * The server owns `ratings`, and applying only the PENDING matches on top keeps
 * it authoritative while still moving the ladder the instant you tap save. With
 * an empty ratings table — a fresh install, or a mirror that has never synced —
 * the whole log is replayed instead, which the domain guarantees is
 * order-independent and identical on every device.
 */
export function effectiveRatings(
  stored: readonly Rating[],
  matches: readonly Match[],
  pending: readonly Mutation[],
): Rating[] {
  if (stored.length === 0) return rebuildRatings(matches)
  const pendingIds = new Set(pending.filter(m => m.kind === 'match.record').map(m => m.id))
  if (pendingIds.size === 0) return [...stored]
  return matches
    .filter(m => pendingIds.has(String(m.id)))
    .reduce<Rating[]>((acc, m) => applyMatch(acc, m), [...stored])
}

// ---------------------------------------------------------------------------
// Small shared UI plumbing
// ---------------------------------------------------------------------------

/**
 * Fire-and-forget for a `mutations.*` call: the local mirror and the outbox are
 * written in one transaction, so there is nothing to await for the UI's sake,
 * but a rejected promise must not become an unhandled rejection.
 */
export function useAction(): (run: () => Promise<unknown>) => void {
  return useCallback((run: () => Promise<unknown>) => {
    void run().catch(() => {
      /* the outbox surfaces rejections; a failed enqueue must not crash a tap */
    })
  }, [])
}

/**
 * An empty state that is composed rather than a bare grey label (docs/03): a
 * mark, the line that says what the state IS, and a hint that says what it
 * means or what to do about it. The line keeps its own element so it reads —
 * and is found — exactly as written.
 */
export function SpaceEmpty({
  children,
  hint,
  mark = '\u2713',
}: {
  children: ReactNode
  hint?: string
  mark?: string
}) {
  return (
    <div className="sp-empty">
      <span className="sp-empty-mark" aria-hidden="true">{mark}</span>
      <p className="sp-empty-line">{children}</p>
      {hint === undefined ? null : <p className="sp-empty-hint">{hint}</p>}
    </div>
  )
}

const STYLE_ID = 'office-space-styles'

/**
 * Styles for the space screens.
 *
 * Injected from here rather than added to the shared stylesheet so this
 * directory owns every file it touches. Every colour is a token from
 * `tokens.css` — there is not one literal colour in this string.
 */
export function SpaceStyles(): null {
  useInsertionEffect(() => {
    if (typeof document === 'undefined') return
    if (document.getElementById(STYLE_ID) !== null) return
    const el = document.createElement('style')
    el.id = STYLE_ID
    el.textContent = SPACE_CSS
    document.head.appendChild(el)
  }, [])
  return null
}

export const SPACE_CSS = `
/* Readable text on the --primary-soft tint. The blue ramp is FIXED across
   themes while the tint flips, so one shade cannot serve both: --blue-700 on
   the light tint is 5.5:1, but on the DARK tint it is dark blue on near-black
   (2.5:1) and fails WCAG AA outright. Light first, dark corrected, and the two
   dark selectors mirror tokens.css so an explicit theme choice still beats the
   system preference. The same pattern as --ph-on-soft and --m-on-primary-soft;
   this sheet is the one that was still painting a fixed ramp shade on a tint
   that flips. */
:root { --sp-on-soft: var(--blue-700); }
:root[data-theme='dark'] { --sp-on-soft: var(--blue-300); }
@media (prefers-color-scheme: dark) {
  :root:not([data-theme='light']):not([data-theme='dark']) {
    --sp-on-soft: var(--blue-300);
  }
}

.sp { display: flex; flex-direction: column; gap: 18px; }

/* The screen's own header sticks BELOW the tab strip, so a long ladder or
   supply list never leaves you wondering which screen you are on. Bled to the
   page gutter so rows scroll UNDER the whole band rather than past its
   shoulders. Two bands both pinned to 0 would sit on top of each other, which
   is why this one is offset by the height of the strip above it. */
.sp-head {
  position: sticky; top: 58px; z-index: 3;
  display: flex; align-items: baseline; justify-content: space-between; gap: 12px;
  margin: 0 -16px; padding: 8px 16px 7px;
  background: color-mix(in srgb, var(--bg) 82%, transparent);
  backdrop-filter: blur(12px) saturate(1.4);
  -webkit-backdrop-filter: blur(12px) saturate(1.4);
}
.sp-head::after {
  content: ''; position: absolute; left: 16px; right: 16px; bottom: 0; height: 1px;
  background: linear-gradient(90deg, var(--blue-300), var(--primary-border) 45%, transparent);
}
/* A header INSIDE a card is not the screen's header: it neither sticks nor
   bleeds, and two stacked sticky bands would fight each other. */
.card > .sp-head, .sp-head-plain {
  position: static; margin: 0; padding: 0; background: none;
  backdrop-filter: none; -webkit-backdrop-filter: none;
}
.card > .sp-head::after, .sp-head-plain::after { content: none; }
/* The mode switch sits above the tabs and is not a screen, so its heading is
   a level quieter than the heading of whichever screen is open. */
.sp-head-plain .sp-title { font-size: 17px; }

.sp-title { font-size: 22px; font-weight: 700; letter-spacing: -0.02em; }
.sp-card-title {
  font-size: 12px; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase;
  color: var(--sp-on-soft);
}
.sp-note { color: var(--text-muted); font-size: 14px; }

/* Never a bare grey label. */
.sp-empty {
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  margin: 0; padding: 24px 20px 26px; text-align: center;
  border: 1px dashed var(--primary-border); border-radius: var(--radius-lg);
  background: linear-gradient(180deg, var(--surface) 0%, var(--surface-tint) 100%);
}
.sp-empty-mark {
  display: grid; place-items: center; width: 46px; height: 46px; border-radius: 999px;
  font-size: 22px; line-height: 1;
  background: var(--primary-soft); color: var(--sp-on-soft);
  border: 1px solid var(--primary-border); box-shadow: var(--ring);
}
.sp-empty-line { margin: 0; font-weight: 640; font-size: 15px; }
.sp-empty-hint {
  margin: 0; max-width: 34ch; color: var(--text-subtle); font-size: 13px; line-height: 1.45;
}

/* Check in / out — the primary presence control, so it is genuinely big. */
.sp-presence-btn {
  width: 100%; min-height: 108px; border-radius: var(--radius-xl);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 4px; font-size: 24px; font-weight: 680; letter-spacing: -0.015em;
  border: 1px solid var(--primary); background: var(--primary);
  color: var(--text-on-primary); box-shadow: var(--shadow);
  transition: transform var(--dur) var(--ease), background var(--dur) var(--ease);
}
.sp-presence-btn:hover { background: var(--primary-hover); border-color: var(--primary-hover); }
.sp-presence-btn:active { transform: translateY(1px); }
.sp-presence-btn[data-present='true'] {
  background: var(--surface); color: var(--text); border-color: var(--border-strong);
}
.sp-presence-btn[data-present='true']:hover { background: var(--surface-sunk); }
.sp-presence-sub { font-size: 14px; font-weight: 500; opacity: .85; }

.sp-person { display: flex; align-items: center; gap: 12px; padding: 10px 0; }
.sp-person + .sp-person { border-top: 1px solid var(--border); }
.sp-avatar {
  width: 42px; height: 42px; border-radius: 999px; flex: none;
  display: grid; place-items: center; font-size: 14px; font-weight: 680;
  background: var(--primary-soft); color: var(--sp-on-soft);
  border: 1px solid var(--primary-border);
}
/* Present gets a ring; away gets none. One glance down the column tells you
   who is actually here. */
.sp-avatar[data-present='true'] { box-shadow: var(--ring); }
.sp-avatar[data-present='false'] {
  background: var(--surface-sunk); color: var(--text-subtle); border-color: var(--border);
  box-shadow: none;
}
.sp-person-name { font-weight: 600; }
.sp-person-meta { display: block; color: var(--text-muted); font-size: 13px; }
.sp-rating {
  min-width: 52px; text-align: right; font-variant-numeric: tabular-nums; font-weight: 700;
}
.sp-fieldset-bare { border: 0; padding: 0; margin: 0; }
.sp-provisional { margin-left: 8px; }
.sp-sticky-foot { margin-top: 10px; }

.sp-list { list-style: none; margin: 0; padding: 0; }
.sp-rows { display: flex; flex-direction: column; }
.sp-row {
  display: flex; align-items: center; gap: 12px; padding: 12px 0;
}
.sp-row + .sp-row { border-top: 1px solid var(--border); }

.sp-seg {
  display: flex; gap: 6px; padding: 4px; border-radius: 999px;
  background: var(--surface-sunk); border: 1px solid var(--border);
}
.sp-seg button {
  flex: 1 1 0; border: none; background: transparent; color: var(--text-muted);
  border-radius: 999px; padding: 9px 10px; font-weight: 600; font-size: 15px;
  transition: background var(--dur) var(--ease), color var(--dur) var(--ease);
}
.sp-seg button[aria-pressed='true'], .sp-seg button[aria-selected='true'] {
  background: var(--surface); color: var(--text); box-shadow: var(--shadow-sm);
}

.sp-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.sp-chip {
  border: 1px solid var(--border-strong); background: var(--surface);
  border-radius: 999px; padding: 8px 14px; font-weight: 550; font-size: 15px;
}
.sp-chip[aria-pressed='true'] {
  background: var(--primary-soft); border-color: var(--primary-border); color: var(--sp-on-soft);
}
.sp-chip:disabled { opacity: .45; }

/* Every tap target on these screens clears 44px. */
.sp .btn, .sp button { min-height: 44px; }
.sp-seg button, .sp-tabs button, .sp-chip { min-height: 44px; }

.sp-banner {
  border-radius: var(--radius); padding: 10px 14px; font-size: 14px;
  background: var(--warn-soft); color: var(--warn); border: 1px solid transparent;
}
.sp-banner[data-tone='info'] {
  background: var(--primary-soft); color: var(--sp-on-soft); border-color: var(--primary-border);
}
.sp-banner[data-tone='ok'] { background: var(--ok-soft); color: var(--ok); }

.sp-countdown {
  font-variant-numeric: tabular-nums; font-size: 30px; font-weight: 700;
  letter-spacing: -0.02em; color: var(--warn);
}
.sp-meter { height: 6px; border-radius: 999px; background: var(--surface-sunk); overflow: hidden; }
.sp-meter > span { display: block; height: 100%; background: var(--warn); }

.sp-figure {
  font-size: 44px; font-weight: 700; line-height: 1; letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
}
.sp-rank {
  width: 26px; text-align: right; color: var(--text-subtle);
  font-variant-numeric: tabular-nums; font-weight: 650;
}
.sp-score-input {
  width: 84px; text-align: center; font-size: 26px; font-weight: 700;
  font-variant-numeric: tabular-nums;
  border: 1px solid var(--border-strong); border-radius: var(--radius);
  background: var(--surface); padding: 8px 4px;
}
.sp-stepper {
  width: 44px; height: 44px; border-radius: 999px; font-size: 22px; line-height: 1;
  border: 1px solid var(--border-strong); background: var(--surface);
}
.sp-textarea {
  width: 100%; min-height: 96px; resize: vertical; padding: 12px 14px;
  border: 1px solid var(--border-strong); border-radius: var(--radius);
  background: var(--surface); font-size: 16px;
}
.sp-select {
  padding: 10px 12px; border-radius: var(--radius);
  border: 1px solid var(--border-strong); background: var(--surface); font-size: 15px;
}
.sp-sticky {
  border: 1px solid var(--border); border-left: 4px solid var(--accent);
  background: linear-gradient(180deg, var(--surface) 0%, var(--surface-tint) 100%);
  border-radius: var(--radius);
  padding: 13px 15px; box-shadow: var(--shadow-sm);
  transition: box-shadow var(--dur) var(--ease);
}
.sp-sticky:hover { box-shadow: var(--shadow); }

@media (prefers-reduced-motion: reduce) {
  .sp-seg button, .sp-presence-btn, .sp-sticky { transition: none; }
}
.sp-sticky-body { white-space: pre-wrap; overflow-wrap: anywhere; }

.sp-tabs {
  position: sticky; top: 0; z-index: 4;
  display: flex; gap: 6px; overflow-x: auto; padding: 6px 0 8px;
  scrollbar-width: none;
  background: color-mix(in srgb, var(--bg) 88%, transparent);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
.sp-tabs::-webkit-scrollbar { display: none; }
.sp-tabs button {
  flex: none; border: 1px solid var(--border); background: var(--surface);
  border-radius: 999px; padding: 8px 15px; font-weight: 600; font-size: 15px;
  color: var(--text-muted);
}
.sp-tabs button[aria-selected='true'] {
  background: linear-gradient(180deg, var(--blue-400), var(--blue-600));
  border-color: var(--blue-600); color: var(--text-on-primary);
  box-shadow: var(--shadow-sm);
}
/* Deleting your own sticky note is a normal action, not an error, so it is a
   quiet button rather than a red one: docs/03 reserves red for a genuine error
   — a failed sync, a rejected booking. The red token is used nowhere below,
   which a test asserts against this whole string. */
.sp-quiet-btn { color: var(--text-muted); border-color: var(--border); background: var(--surface); }
.sp-quiet-btn:hover { color: var(--text); background: var(--surface-sunk); }
`
